/**
 * <searchanise-facets>
 *
 * Filters a collection through the Searchanise (Snize) index while the product
 * cards keep coming from the theme.
 *
 * Search & Discovery was uninstalled when Searchanise was adopted, and S&D is what
 * creates storefront filters — so Shopify now offers none on any collection, at any
 * size. Searchanise
 * indexes the whole catalogue, so it answers two questions: which facets exist
 * with which counts, and which product ids match. The cards themselves are then
 * rendered by sections/product-grid-by-ids.liquid through the Section Rendering
 * API, so card-product is untouched and Rubik, Judge.me, Regios, wishlist and
 * quick view keep working.
 *
 * Two scopes, one element (data-scope):
 *
 *   collection  Unfiltered, the page is left exactly as the server rendered it —
 *               native grid, native count, native pagination — so crawlers and
 *               first paint are unaffected. The first filter switches the grid
 *               over to Searchanise; removing the last one reloads back to it.
 *               Sorting an unfiltered collection reloads too, so that Shopify
 *               keeps doing it — the theme's own sort is dead here, because it
 *               answers through the facet form this element replaces.
 *   search      There is nothing worth keeping: the native route caps German
 *               searches at 1.000 hits. The element owns the page from the first
 *               paint and also renders the collection and page hits, which the
 *               tabs switch between.
 *
 * The URL is the only source of truth for filter state:
 *   sf.q=… / q=…      free text — sf.q within a collection, the native q on the
 *                     search page, so the theme's own search form keeps working
 *   sf.<attribute>=…  repeated once per selected value
 *   sf.price=min,max  range
 *   tab=…             products | collections | pages (search only)
 *   page, sort_by     as the theme already uses them
 *
 * Verified API behaviour this relies on (staging, 2026-09-15):
 *   - restrictBy[attr][]=a&restrictBy[attr][]=b  → OR within a facet
 *   - separate restrictBy attributes             → AND across facets
 *   - restrictBy[price]=10.0000,20.0000          → range (comma, not pipe)
 *   - a comma inside a value is NOT a separator; "Redken,Wella" matches nothing
 *   - maxResults=0 returns totalItems:0 even with items=true, so never ask for 0
 *   - facets self-exclude: restricting vendor leaves the vendor buckets intact
 *
 * Verified additions for the global search (staging, 2026-09-17):
 *   - categories=true&pages=true&suggestions=true add categories[], pages[] and
 *     suggestions[] to the same response, alongside totalCategories/totalPages
 *   - categoriesMaxResults / pagesMaxResults cap at 50 each, paged through
 *     categoryStartIndex / pageStartIndex
 *   - an empty q without a collection returns the whole catalogue with facets,
 *     so the bare /search page is a normal request, not a special case
 */
if (!customElements.get('searchanise-facets')) {
  const PREFIX = 'sf.';
  const PRICE_KEY = `${PREFIX}price`;
  const QUERY_KEY = `${PREFIX}q`;
  const TABS = ['products', 'collections', 'pages'];

  /**
   * Theme sort_by values mapped onto Searchanise sortBy/sortOrder pairs.
   *
   * `manual` is deliberately absent. It is a collection's hand-sorted order,
   * which only Shopify knows; mapping it onto sales_amount would serve
   * best-selling under a label promising something else. The option is hidden
   * in snippets/facet-short.liquid, and anything not in this map is treated as
   * no sort at all — so an old link carrying sort_by=manual falls back to the
   * server-rendered order rather than to an approximation of it.
   */
  const SORT_MAP = {
    'best-selling': ['sales_amount', 'desc'],
    relevance: ['relevance', 'desc'],
    'title-ascending': ['title', 'asc'],
    'title-descending': ['title', 'desc'],
    'price-ascending': ['price', 'asc'],
    'price-descending': ['price', 'desc'],
    'created-ascending': ['created', 'asc'],
    'created-descending': ['created', 'desc'],
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  /**
   * A custom element whose constructor reads its own children — <price-range>
   * and <show-more-button> both do — must not be built with createElement: the
   * constructor runs on the spot, before anything has been appended, and dies on
   * a null child. Parsing the tag inside a <template> keeps the node inert,
   * because a template's document has no browsing context and so upgrades
   * nothing. The upgrade happens when the finished node is inserted into the
   * page, by which time the children it looks for are there.
   *
   * This only bites after facets.js and show-more.js have run. On first paint
   * they have not defined their elements yet, so createElement is harmless and
   * the upgrade comes later, correctly — which is why the sidebar renders fine
   * on load and only breaks from the first filter click onwards.
   *
   * It is also the only way to build a customized built-in such as
   * <details is="accordion-details">. `is` is read when the element is created
   * and never again: setting the attribute afterwards leaves a plain <details>
   * that looks right in the inspector and behaves like nothing at all.
   */
  const inert = (tag, className, attributes) => {
    const template = document.createElement('template');
    const markup = Object.entries(attributes || {})
      .map(([name, value]) => ` ${name}="${value}"`)
      .join('');

    template.innerHTML = `<${tag}${markup}></${tag}>`;
    const node = template.content.firstElementChild;
    if (className) node.className = className;
    return node;
  };

  class SearchaniseFacets extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
      this.icons = {};
      this.querySelectorAll('template[data-icon]').forEach((template) => {
        this.icons[template.dataset.icon] = template;
      });

      this.scope = this.dataset.scope === 'search' ? 'search' : 'collection';
      this.pageSize = Number(this.dataset.pageSize) || 24;
      // The API returns at most 50 categories or pages per request.
      this.sideSize = Math.min(Number(this.dataset.sideSize) || 24, 50);
      this.cardClass = this.dataset.cardClass || 'product-card';
      this.showMoreAfter = Number(this.dataset.showMoreAfter) || 10;
      this.expandGroups = (this.dataset.expandGroups || '')
        .split(',')
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);

      this.cache = new Map();
      this.state = this.readState();

      // Both of these are worth one line in the console — between them they name
      // the two ways this goes wrong on a pushed theme, and neither shows up as
      // an error anywhere else. A key from the wrong shop does not fail: it
      // answers, with totalItems 0.
      const embedKey = window.Searchanise?.ApiKey;
      const metafieldKey = this.dataset.apiKey;

      if (metafieldKey && embedKey && metafieldKey !== embedKey) {
        console.info(
          `[searchanise-facets] using the shop metafield's key ${metafieldKey}, not the app embed's ${embedKey}`
        );
      }
      // No metafield and no embed means the theme setting is answering, and that
      // one travelled here with the theme — so it belongs to whichever shop the
      // theme came from, not to this one.
      if (!metafieldKey && !embedKey && this.dataset.apiKeyFallback) {
        console.warn(
          `[searchanise-facets] falling back to the theme setting's key ${this.dataset.apiKeyFallback} — ` +
            'set the shop metafield custom.searchanise_api_key, or enable the Searchanise app embed'
        );
      }

      document.addEventListener('change', this.onChange.bind(this), true);
      document.addEventListener('click', this.onClick.bind(this));

      this.init();
    }

    // ---------------------------------------------------------------- state

    /**
     * Which Searchanise installation to ask, in order:
     *
     *   1. `data-api-key`           the shop metafield custom.searchanise_api_key
     *   2. `window.Searchanise`     the installed app embed
     *   3. `data-api-key-fallback`  the theme setting
     *
     * Staging and production are separate installations with separate keys, and
     * everything the theme carries travels with it on a push — so a key stored
     * in the theme settings is the one thing guaranteed to be wrong on the shop
     * it lands on. That is not hypothetical: staging ran on production's key
     * (7p9s4o5j8P) against staging's catalog id and got totalItems 0 back, which
     * is what a foreign key plus a valid catalog id looks like from the outside.
     *
     * The metafield lives in the shop, so it stays behind and is right per
     * environment by construction. The embed's key is right too whenever the
     * embed renders — it parses it out of its own script URL — but it is only as
     * reliable as the embed, and an embed that fails to render takes the key
     * with it. So the metafield leads and the embed backs it up.
     *
     * The theme setting is last and exists only so an unconfigured shop is not
     * dead in the water. If it is ever the one answering, something upstream is
     * already wrong.
     */
    get apiKey() {
      return this.dataset.apiKey || window.Searchanise?.ApiKey || this.dataset.apiKeyFallback || '';
    }

    /**
     * Where the free text lives. Inside a collection it is ours to name, but on
     * the search page it has to stay the native q: that is what the theme's
     * search form submits and what a shared link is expected to carry.
     */
    get queryKey() {
      return this.scope === 'search' ? 'q' : QUERY_KEY;
    }

    /** Filter state lives in the URL so back/forward and deep links just work. */
    readState() {
      const params = new URLSearchParams(window.location.search);
      const filters = new Map();

      for (const [key, value] of params) {
        if (!key.startsWith(PREFIX) || key === PRICE_KEY || key === QUERY_KEY) continue;
        const attribute = key.slice(PREFIX.length);
        if (!filters.has(attribute)) filters.set(attribute, new Set());
        filters.get(attribute).add(value);
      }

      const price = params.get(PRICE_KEY);
      const [min, max] = (price || '').split(',');

      const tab = params.get('tab');

      // Only sorts this element can actually carry out; see SORT_MAP.
      const sort = params.get('sort_by');

      return {
        q: params.get(this.queryKey) || '',
        tab: this.scope === 'search' && TABS.includes(tab) ? tab : 'products',
        page: Math.max(Number(params.get('page')) || 1, 1),
        sort: sort && SORT_MAP[sort] ? sort : null,
        filters,
        price: price && min && max ? { min: Number(min), max: Number(max) } : null,
      };
    }

    writeState({ replace = false } = {}) {
      const url = new URL(window.location.pathname, window.location.origin);

      if (this.state.q) url.searchParams.set(this.queryKey, this.state.q);
      if (this.state.tab !== 'products') url.searchParams.set('tab', this.state.tab);
      for (const [attribute, values] of this.state.filters) {
        for (const value of values) url.searchParams.append(PREFIX + attribute, value);
      }
      if (this.state.price) url.searchParams.set(PRICE_KEY, `${this.state.price.min},${this.state.price.max}`);
      if (this.state.sort) url.searchParams.set('sort_by', this.state.sort);
      if (this.state.page > 1) url.searchParams.set('page', String(this.state.page));

      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({ searchanise: true }, '', url.toString());
    }

    /** True once the visitor has narrowed anything — the point where we take over the grid. */
    get isFiltered() {
      return Boolean(this.state.q || this.state.filters.size || this.state.price);
    }

    /**
     * Whether the results on screen are ours. On a collection that starts false
     * and flips with the first filter; on the search page there is no native
     * render to defer to, so it is true from the start.
     */
    get ownsPage() {
      return this.scope === 'search' || this.isFiltered;
    }

    /**
     * Searchanise has no equivalent for a collection's hand-sorted order, so the
     * moment it takes over, the order has to become one it can actually express.
     * best-selling is the closest thing to “the order the merchant chose”.
     *
     * Flagged as automatic: when the last filter goes, an order nobody asked for
     * should go with it, while an order the visitor picked should not.
     */
    ensureSortableOrder() {
      if (this.scope !== 'collection' || !this.isFiltered || this.state.sort) return;
      this.state.sort = 'best-selling';
      this.autoSort = true;
    }

    /**
     * Nothing is narrowed any more: hand the page back to the server-rendered
     * grid, which is the only thing that can produce the hand-sorted order.
     * Returns true when it has taken over, so callers stop.
     */
    handBackIfUnfiltered() {
      if (this.ownsPage) return false;

      if (this.autoSort) {
        this.state.sort = null;
        this.autoSort = false;
      }

      this.writeState();
      window.location.reload();
      return true;
    }

    // -------------------------------------------------------------- requests

    /**
     * One Searchanise request. `maxResults` is never 0 and `items` is never
     * false: either makes the API report totalItems as 0, which would blank the
     * product count and every tab badge.
     */
    buildUrl({ maxResults = 1 } = {}) {
      const url = new URL(this.dataset.endpoint);
      const params = url.searchParams;

      params.set('api_key', this.apiKey);
      params.set('q', this.state.q);
      // `collection=<handle>` and `restrictBy[collection_ids]` return different
      // sets (17.056 vs 10.149 for haarpflege); the handle is the correct one.
      // Absent on the search page — that is what makes the query global.
      if (this.dataset.collection) params.set('collection', this.dataset.collection);
      // An empty value is not the same as no value — send it only when there is
      // one, and let the index answer with its default set otherwise.
      if (this.dataset.marketCatalogs) {
        params.set('restrictBy[shopify_market_catalogs]', this.dataset.marketCatalogs);
      }
      params.set('union[price][min]', this.dataset.priceField);
      params.set('shopify_currency', this.dataset.currency);
      params.set('shopify_country', this.dataset.country);
      params.set('timeZoneName', this.dataset.timezone);
      params.set('prepareVariantOptions', 'true');
      params.set('facets', 'true');
      params.set('facetsShowUnavailableOptions', 'false');
      params.set('output', 'json');

      // Array form rather than a pipe-joined string: values legitimately contain
      // punctuation, and this way nothing has to be escaped.
      for (const [attribute, values] of this.state.filters) {
        for (const value of values) params.append(`restrictBy[${attribute}][]`, value);
      }
      if (this.state.price) {
        params.set('restrictBy[price]', `${this.state.price.min.toFixed(4)},${this.state.price.max.toFixed(4)}`);
      }

      const [sortBy, sortOrder] = SORT_MAP[this.state.sort] || [];
      if (sortBy) {
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);
      }

      // Only the tab in view pages; the others stay at their first page so their
      // counts still arrive with every response for the tab bar.
      const onTab = (tab) => this.state.tab === tab;
      const offset = (page, size) => String((page - 1) * size);

      params.set('startIndex', onTab('products') ? offset(this.state.page, this.pageSize) : '0');
      params.set('maxResults', String(Math.max(maxResults, 1)));
      // Always true, never configurable: items=false makes the API report
      // totalItems as 0, the same trap as maxResults=0, and that number is what
      // the product count and the tab badges are built from.
      params.set('items', 'true');

      if (this.scope === 'search') {
        // Inside the ranking window the whole window is fetched and paged here,
        // after ranking; see rankCollections.
        const ranked = onTab('collections') && this.inRankWindow;
        params.set('categories', 'true');
        params.set('categoriesMaxResults', String(ranked ? this.rankWindow : this.sideSize));
        params.set(
          'categoryStartIndex',
          onTab('collections') && !ranked ? offset(this.state.page, this.sideSize) : '0'
        );
        params.set('pages', 'true');
        params.set('pagesMaxResults', String(this.sideSize));
        params.set('pageStartIndex', onTab('pages') ? offset(this.state.page, this.sideSize) : '0');
        // Only ever shown when a search comes back empty, but it costs nothing
        // to carry and saves a second request at exactly the wrong moment.
        params.set('suggestions', 'true');
        params.set('suggestionsMaxResults', '6');
      }

      return url.toString();
    }

    async fetchSnize(options) {
      const url = this.buildUrl(options);
      if (this.cache.has(url)) return this.cache.get(url);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Searchanise responded ${response.status}`);

      const data = await response.json();
      this.cache.set(url, data);
      return data;
    }

    /**
     * Cards for a batch of ids, rendered by the theme.
     *
     * Shopify ignores the order the ids were given in, so the columns are
     * re-sorted here by data-grid-product-id. That attribute is deliberately not
     * data-product-id: card-product puts data-product-id on the compare checkbox
     * and the wishlist button inside every card, so a [data-product-id] lookup
     * ends up holding those inner inputs instead of the column — and injecting
     * them leaves a grid that looks empty.
     *
     * Keep the batch within the renderer's page size or the tail is silently
     * dropped.
     */
    async fetchCards(ids) {
      if (!ids.length) return [];

      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('section_id', this.dataset.gridSection);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Section render responded ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const byId = new Map();
      parsed.querySelectorAll('[data-grid-product-id]').forEach((column) => {
        byId.set(column.dataset.gridProductId, column);
      });

      return ids.map((id) => byId.get(String(id))).filter(Boolean);
    }

    // --------------------------------------------------------------- render

    icon(name) {
      const template = this.icons[name];
      return template ? template.content.cloneNode(true) : document.createDocumentFragment();
    }

    countLabel(total) {
      const template = total === 1 ? this.strings.productCountOne : this.strings.productCountOther;
      return (template || '{{ count }}').replace(/[\d.,]+/, total.toLocaleString());
    }

    /**
     * One facet group, in the markup of snippets/facets.liquid so that
     * collection.css, accordion-details and show-more.js all apply unchanged.
     */
    buildFacet(facet, context, previous) {
      // accordion-details is a customized built-in (class AccordionDetails
      // extends HTMLDetailsElement), so the `is` has to be there when the
      // element is created — hence inert() rather than createElement. It brings
      // the slide open/close and, through the aria-expanded it maintains, the
      // CSS that rotates the plus into a minus.
      const details = inert('details', 'accordion-details facet-accordion-details', {
        is: 'accordion-details',
      });
      details.dataset.filter = '';
      details.dataset.index = `${context}-${facet.attribute}`;
      // What the visitor opened by hand outranks the configured default — after
      // the first click the default is no longer what they are looking at.
      details.open = previous
        ? previous.open
        : this.expandGroups.includes((facet.title || '').toLowerCase());

      const summary = el('summary', 'accordion-details__summary flex items-center justify-between');
      summary.append(el('span', 'h6', facet.title), this.icon('plus-toggle'));
      details.append(summary);

      const expanded = Boolean(previous && previous.expanded);
      const content =
        facet.type === 'select'
          ? this.buildSelectFacet(facet, context, expanded)
          : this.buildRangeFacet(facet, context);

      // AccordionDetails points aria-controls at this element whenever `open`
      // changes, so it needs something to point at.
      content.id = `SnizeFacet-${context}-${facet.attribute}`;

      details.append(content);
      return details;
    }

    buildSelectFacet(facet, context, expanded) {
      const content = el('div', 'accordion-details__content parent-display flex flex-col gap-3');
      const list = el('ul', 'grid gap-3');
      const selected = this.state.filters.get(facet.attribute) || new Set();

      facet.buckets.forEach((bucket, index) => {
        const id = `Snize-${context}-${facet.attribute}-${index}`;
        // The class stays on the overflow items even while they are shown, because
        // show-more.js toggles `hidden` on exactly that selector — and it is also
        // how snapshotFacetState recognises an expanded group.
        const overflow = index >= this.showMoreAfter;
        const item = el(
          'li',
          `flex items-center gap-2${overflow ? ' show-more-item' : ''}${overflow && !expanded ? ' hidden' : ''}`
        );

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'checkbox';
        input.id = id;
        input.dataset.snizeAttribute = facet.attribute;
        input.value = bucket.value;
        input.checked = selected.has(String(bucket.value));

        const label = el('label', 'flex flex-grow justify-between items-center gap-1 text-subtext reversed-link');
        label.htmlFor = id;
        label.append(
          el('span', 'reversed-link__text', bucket.value),
          el('span', 'count', String(bucket.count))
        );

        item.append(input, label);
        list.append(item);
      });

      content.append(list);

      if (facet.buckets.length > this.showMoreAfter) {
        const wrapper = inert('show-more-button');
        const button = el('button', 'button-show-more btn--link');
        button.type = 'button';
        // show-more.js locates the group via closest('[id^="Show-More-"]').
        button.id = `Show-More-${facet.attribute}-${context}`;
        button.append(
          el('span', `label-show-more label-text${expanded ? ' hidden' : ''}`, this.strings.showMore),
          el('span', `label-show-less label-text${expanded ? '' : ' hidden'}`, this.strings.showLess)
        );
        wrapper.append(button);
        content.append(wrapper);
      }

      return content;
    }

    /**
     * Price. The names filter.v.price.gte / filter.v.price.lte are not optional:
     * the theme's <price-range> element looks them up by exactly those names.
     */
    buildRangeFacet(facet, context) {
      // The slider bucket's upper bound has never been pinned down against a live
      // response — the doc records the facet shape only as { value, count }. Read
      // whichever of the plausible keys is actually a number, so a naming surprise
      // costs the slider nothing.
      const bucket = facet.buckets[0] || {};
      const upperBound = [bucket.right, bucket.to, bucket.max, bucket.end, facet.max, facet.right].find(
        (candidate) => candidate != null && !Number.isNaN(Number(candidate))
      );
      const rangeMax = Math.ceil(Number(upperBound ?? 0));
      const current = this.state.price || { min: 0, max: rangeMax };

      const content = el('div', 'accordion-details__content accordion-details__content--price');
      const range = inert('price-range', 'grid');

      const wrapper = el('div', 'facet-range-wrapper relative');
      wrapper.style.setProperty('--range-min', `${(current.min / (rangeMax || 1)) * 100}%`);
      wrapper.style.setProperty('--range-max', `${(current.max / (rangeMax || 1)) * 100}%`);

      const slider = (className, id, value) => {
        const input = document.createElement('input');
        input.type = 'range';
        input.className = className;
        input.id = id;
        input.min = '0';
        input.max = String(rangeMax);
        input.step = '1';
        input.value = String(Math.round(value));
        return input;
      };
      wrapper.append(
        slider('facet-range facet-range__min w-full pointer-events-none', `SnizeRangeFrom-${context}`, current.min),
        slider('facet-range facet-range__max w-full pointer-events-none absolute', `SnizeRangeTo-${context}`, current.max)
      );

      const inputs = el('div', 'facet-input-wrapper flex items-center gap-4');
      const numberField = (name, id, value, placeholder) => {
        const field = el('div', 'flex items-center flex-grow relative gap-2');
        const input = document.createElement('input');
        input.type = 'number';
        input.inputMode = 'numeric';
        input.className = 'form-control facet-range__input';
        input.name = name;
        input.id = id;
        input.autocomplete = 'off';
        input.min = '0';
        input.max = String(rangeMax);
        input.step = '1';
        input.placeholder = placeholder;
        if (this.state.price) input.value = String(Math.round(value));
        field.append(el('span', 'facet-range__symbol', this.dataset.currency), input);
        return field;
      };
      inputs.append(
        numberField('filter.v.price.gte', `SnizeInputFrom-${context}`, current.min, '0'),
        numberField('filter.v.price.lte', `SnizeInputTo-${context}`, current.max, String(rangeMax))
      );

      range.append(wrapper, inputs);
      content.append(range);
      this.priceMax = rangeMax;
      return content;
    }

    /**
     * What the visitor has opened in the sidebar, keyed the way buildFacet keys
     * its groups. The counts change with every request, so the sidebar is rebuilt
     * each time — without this the group under the cursor snaps shut on the click
     * that opened it, and every expanded “show more” collapses again.
     */
    snapshotFacetState(target) {
      const state = new Map();
      target.querySelectorAll('details[data-index]').forEach((details) => {
        state.set(details.dataset.index, {
          open: details.open,
          expanded: Boolean(details.querySelector('.show-more-item:not(.hidden)')),
        });
      });
      return state;
    }

    /**
     * The drawer's own sort dropdown, cloned from the one in the toolbar.
     *
     * On the search page the toolbar dropdown is ours — Shopify's search route
     * only offers relevance and price, so the section builds the full list from
     * a collection's options. Rather than render that twice in Liquid, the
     * drawer gets a copy, which also guarantees the two can never drift.
     */
    buildDrawerSort() {
      if (this.scope !== 'search') return null;

      const source = document.querySelector('#SortByContainer');
      if (!source) return null;

      const wrapper = el('div', 'flex items-center gap-3 lg:hidden facet-short-drawer');
      wrapper.id = 'SortByContainerMobile';

      // Spread first: `children` is live, and appending each node removes it from
      // the clone mid-iteration, which would silently skip every second one.
      wrapper.append(...source.cloneNode(true).children);

      // The clone brought the toolbar's ids with it; two elements answering to
      // SortBy-search would make the label point at the wrong control.
      wrapper.querySelectorAll('[id]').forEach((node) => {
        node.id = `${node.id}-drawer`;
      });
      wrapper.querySelectorAll('label[for]').forEach((label) => {
        label.htmlFor = `${label.htmlFor}-drawer`;
      });

      return wrapper;
    }

    /**
     * Both dropdowns show what the URL actually asks for — and only orders that
     * can actually be produced.
     *
     * “Ausgewählt” (manual) is the collection's hand-sorted order, which lives in
     * Shopify and nowhere else. It belongs to the server-rendered grid, so it is
     * offered exactly while that grid is on screen and withdrawn the moment
     * Searchanise takes over. Clearing the last filter reloads the page, which
     * brings it back.
     *
     * hidden drops it from the list, disabled keeps it unselectable in anything
     * that ignores hidden on an <option>.
     */
    syncSortSelects() {
      const offerManual = this.scope === 'collection' && !this.isFiltered;

      document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        const manual = select.querySelector('option[value="manual"]');
        if (manual) {
          manual.hidden = !offerManual;
          manual.disabled = !offerManual;
        }

        const wanted = this.state.sort;
        if (wanted && select.value !== wanted) select.value = wanted;
      });
    }

    renderFacets(facets) {
      const targets = [
        [document.querySelector('#FacetFiltersContainer .sticky-element') || document.getElementById('FacetFiltersContainer'), 'vertical'],
        [document.getElementById('DrawerFacetFiltersContainer'), 'drawer'],
      ];

      for (const [target, context] of targets) {
        if (!target) continue;

        const previous = this.snapshotFacetState(target);
        const isDrawer = context === 'drawer';
        const fragment = document.createDocumentFragment();
        for (const facet of facets) {
          if (!facet.buckets || !facet.buckets.length) continue;
          fragment.append(this.buildFacet(facet, context, previous.get(`${context}-${facet.attribute}`)));
        }

        // Only the facet groups belong to us. The drawer also holds the mobile
        // sort dropdown and the sticky apply button, which must survive.
        const before = target.querySelector('#SortByContainerMobile') || (isDrawer ? this.buildDrawerSort() : null);
        const after = target.querySelector('.drawer__sticky');

        target.replaceChildren();
        if (before) target.append(before);
        target.append(fragment);
        if (after) target.append(after);
      }
    }

    renderChips() {
      const container = document.getElementById('FacetFiltersActive');
      if (!container) return;

      const fragment = document.createDocumentFragment();
      const chip = (label, onRemove) => {
        const link = el('a', 'facet-remove btn btn--secondary flex relative');
        link.href = '#';
        link.dataset.snizeRemove = JSON.stringify(onRemove);
        const inner = el('div', 'btn__text flex items-center justify-between gap-2');
        inner.append(el('div', 'facet-title text-wrap flex items-center gap-2', label), this.icon('close'));
        link.append(inner);
        return link;
      };

      let count = 0;
      for (const [attribute, values] of this.state.filters) {
        for (const value of values) {
          fragment.append(chip(value, { attribute, value }));
          count += 1;
        }
      }
      if (this.state.price) {
        fragment.append(chip(`${this.state.price.min} – ${this.state.price.max}`, { price: true }));
        count += 1;
      }
      if (this.state.q) {
        fragment.append(chip(`\u201c${this.state.q}\u201d`, { query: true }));
        count += 1;
      }

      // The drawer button carries the same number on mobile, where the chips
      // themselves are out of sight behind the drawer.
      document.querySelectorAll('facet-count').forEach((node) => {
        node.textContent = `(${count})`;
        node.hidden = count === 0;
      });

      if (count > 1) {
        const clear = el('a', 'facet-clear-all btn--link', this.strings.clearAll);
        clear.href = '#';
        clear.dataset.snizeRemove = JSON.stringify({ all: true });
        fragment.append(clear);
      }

      container.replaceChildren(fragment);
    }

    renderPagination(total, container, pageSize) {
      if (!container) return;

      container.querySelector('.pagination')?.remove();

      const pages = Math.ceil(total / pageSize);
      if (pages <= 1) return;

      const nav = el('nav', 'pagination');
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', this.strings.paginationLabel || 'Pagination');
      const list = el('ul', 'pagination__list list-unstyled flex items-center flex-wrap justify-center');
      list.setAttribute('role', 'list');

      const pageLink = (page, label, extraClass, icon) => {
        const item = document.createElement('li');
        const link = el('a', `pagination__item${extraClass ? ` ${extraClass}` : ''}`);
        link.href = '#';
        link.dataset.snizePage = String(page);
        if (icon === 'before') link.append(this.icon('caret-left'));
        link.append(el('span', null, label));
        if (icon === 'after') link.append(this.icon('caret-right'));
        item.append(link);
        return item;
      };

      if (this.state.page > 1) {
        list.append(pageLink(this.state.page - 1, this.strings.previous, 'pagination__item--previous pagination__item-arrow motion-reduce', 'before'));
      }

      // A compact window rather than every page: 17.000 products is 700+ pages.
      const from = Math.max(1, this.state.page - 2);
      const to = Math.min(pages, this.state.page + 2);
      for (let page = from; page <= to; page += 1) {
        if (page === this.state.page) {
          const item = document.createElement('li');
          const current = el('a', 'pagination__item pagination__item--current background-2', String(page));
          current.setAttribute('role', 'link');
          current.setAttribute('aria-disabled', 'true');
          current.setAttribute('aria-current', 'page');
          item.append(current);
          list.append(item);
        } else {
          list.append(pageLink(page, String(page)));
        }
      }

      if (this.state.page < pages) {
        list.append(pageLink(this.state.page + 1, this.strings.next, 'pagination__item--next pagination__item-arrow motion-reduce', 'after'));
      }

      nav.append(list);
      container.append(nav);
    }

    /**
     * The tab bar. One response carries all three totals, so the counts are
     * always consistent with each other — and a tab with nothing behind it is
     * not worth offering, apart from products, which stays as the way back.
     */
    renderTabs(data) {
      const bar = document.getElementById('SnizeTabs');
      if (!bar) return;

      const totals = {
        products: Number(data.totalItems) || 0,
        collections: Number(data.totalCategories) || 0,
        pages: Number(data.totalPages) || 0,
      };

      for (const tab of TABS) {
        const count = bar.querySelector(`[data-snize-tab-count="${tab}"]`);
        if (count) count.textContent = totals[tab] ? `(${totals[tab].toLocaleString()})` : '';

        const button = bar.querySelector(`[data-snize-tab="${tab}"]`);
        if (!button) continue;
        button.setAttribute('aria-selected', String(tab === this.state.tab));
        // An empty tab is not worth offering — but never pull the one the visitor
        // is standing on out from under them, or the empty state has no label.
        const empty = totals[tab] === 0 && tab !== 'products' && tab !== this.state.tab;
        button.classList.toggle('snize-hidden', empty);
      }

      document.querySelectorAll('[data-snize-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.snizePanel !== this.state.tab;
      });

      // Facets, sorting and the chips describe the product query alone. A class
      // rather than [hidden], because the sidebar carries xl:block and would win.
      document.querySelectorAll('[data-snize-panel-chrome]').forEach((node) => {
        node.classList.toggle('snize-hidden', node.dataset.snizePanelChrome !== this.state.tab);
      });
    }

    /**
     * The first collections Searchanise returns, re-ordered by the ranking in
     * assets/search-category-ranking.js. Ranking moves collections across page
     * boundaries, so server paging inside this window would show some twice and
     * others never: the window is fetched in one request and paged here. It is
     * a whole number of pages at most the API's 50, so the first server-paged
     * page starts exactly where it ends. Beyond it, each page is ranked on its own.
     */
    get rankWindow() {
      return Math.floor(50 / this.sideSize) * this.sideSize;
    }

    get inRankWindow() {
      return Boolean(window.SearchCategoryRanking?.isActive()) && (this.state.page - 1) * this.sideSize < this.rankWindow;
    }

    rankCollections(categories) {
      const ranking = window.SearchCategoryRanking;
      if (!ranking?.isActive()) return categories;

      const ranked = ranking.apply(categories, this.state.q);
      if (!this.inRankWindow) return ranked;

      const start = (this.state.page - 1) * this.sideSize;
      return ranked.slice(start, start + this.sideSize);
    }

    /**
     * Collections and pages. Shopify's search route returns neither as a
     * renderable object — collections are not searchable at all — so unlike the
     * products these cards are built from the Searchanise JSON, in the theme's
     * own card markup.
     */
    renderSideResults(kind, entries, total, data) {
      const names = {
        collections: ['SnizeCollectionsList', 'SnizeCollectionsContainer'],
        pages: ['SnizePagesList', 'SnizePagesContainer'],
      }[kind];
      const list = document.getElementById(names[0]);
      const container = document.getElementById(names[1]);
      if (!list || !container) return;

      container.querySelector('.empty-state')?.remove();

      if (entries.length) {
        const build = kind === 'collections' ? this.buildCollectionCard : this.buildPageCard;
        list.replaceChildren(...entries.map((entry) => build.call(this, entry)));
      } else {
        list.replaceChildren();
        // No filter hint here: the facets narrow products, never these.
        container.prepend(this.buildEmptyState(data, { hint: false }));
      }

      this.renderPagination(total, container, this.sideSize);
    }

    /**
     * Searchanise hands back image_link, title, link and description as plain
     * strings — the descriptions carry HTML entities, so they go through the
     * browser's own decoder before landing in textContent.
     */
    decode(value) {
      if (!value) return '';
      // Page descriptions arrive as raw body text: markup first, then the entity
      // decode (a textarea parses its content as text, so nothing is executed).
      const area = document.createElement('textarea');
      area.innerHTML = String(value).replace(/<[^>]*>/g, ' ');
      return area.value.replace(/\s+/g, ' ').trim();
    }

    buildImage(source, alt, width) {
      if (!source) return null;
      const image = document.createElement('img');
      const url = new URL(source, window.location.origin);
      // Shopify's CDN sizes on request; the raw link is the full-size original.
      url.searchParams.set('width', String(width));
      image.src = url.toString();
      image.alt = alt || '';
      image.loading = 'lazy';
      image.className = 'motion-reduce';
      return image;
    }

    buildCollectionCard(entry) {
      const column = el('div', 'f-column');
      const card = el('div', 'collection-card collection-card--standard');

      const link = el('a', 'collection-card__wrapper relative flex flex-col items-center justify-start text-center focus-inset');
      link.href = entry.link || '#';

      const media = el('div', 'collection-card__image media-wrapper rounded');
      media.style.setProperty('--aspect-ratio', '1');
      const image = this.buildImage(entry.image_link, entry.title, 400);
      media.append(image || el('div', 'collection-card__placeholder'));

      const inner = el('div', 'collection-card__inner w-full');
      const title = el('h3', 'collection-card__title h6 flex-1');
      title.append(el('span', null, this.decode(entry.title)));
      inner.append(title);

      link.append(media, inner);
      card.append(link);
      column.append(card);
      return column;
    }

    buildPageCard(entry) {
      const column = el('div', 'f-column card');
      const card = el('div', this.cardClass);
      const wrapper = el('div', 'product-card__wrapper h-full');

      const link = el('a', 'full-width-link product-card__link');
      link.href = entry.link || '#';
      link.append(el('span', 'visually-hidden', this.decode(entry.title)));

      const info = el('div', 'product-card__info h-full grid gap-2');
      info.append(el('h3', 'product-card__title text-pcard-title', this.decode(entry.title)));

      const excerpt = this.decode(entry.description);
      if (excerpt) info.append(el('p', 'm-0 text-subtext text-limit-3-line', excerpt.slice(0, 200)));

      wrapper.append(link, info);
      card.append(wrapper);
      column.append(card);
      return column;
    }

    // ---------------------------------------------------------- orchestration

    setLoading(on) {
      document
        .querySelectorAll('[data-facet-loading]')
        .forEach((node) => node.classList.toggle('btn--loading', on));
    }

    /**
     * First paint. With nothing filtered the server-rendered page is already
     * correct, so only the facet sidebar is filled in — the grid, the count and
     * the pagination are left alone. That keeps the page crawlable and avoids a
     * flash of replaced content.
     */
    async init() {
      if (!this.apiKey) {
        // Silence beats a broken sidebar, but not without saying why: without a
        // key every request would 4xx and the page would look inexplicably dead.
        console.warn(
          '[searchanise-facets] no API key — set one under Settings → Searchanise, or enable the Searchanise app embed'
        );
        return;
      }

      try {
        if (this.ownsPage) {
          this.ensureSortableOrder();
          await this.apply({ push: false });
        } else {
          const data = await this.fetchSnize({ maxResults: 1 });
          this.renderFacets(data.facets || []);
          this.renderChips();
        }
      } catch (error) {
        // Leave the native page standing rather than showing a broken sidebar.
        console.error('[searchanise-facets]', error);
      }
    }

    /** Run the current state: fetch matches, render results, update the chrome. */
    async apply({ push = true } = {}) {
      this.setLoading(true);

      try {
        const onProducts = this.state.tab === 'products';
        // The request shape depends on the ranking (see rankWindow), so it has to
        // be known first. Usually already there: it loads at idle from every page.
        if (this.state.tab === 'collections') await window.SearchCategoryRanking?.load();
        // Off the products tab the items are never shown, but totalItems still
        // has to arrive for the tab count — and maxResults 0 would report it as 0.
        const data = await this.fetchSnize({ maxResults: onProducts ? this.pageSize : 1 });

        this.renderFacets(data.facets || []);
        this.renderChips();
        this.renderTabs(data);
        this.syncSortSelects();

        if (onProducts) {
          await this.renderProducts(data);
        } else if (this.state.tab === 'collections') {
          this.renderSideResults(
            'collections',
            this.rankCollections(data.categories || []),
            Number(data.totalCategories) || 0,
            data
          );
        } else {
          this.renderSideResults('pages', data.pages || [], Number(data.totalPages) || 0, data);
        }

        this.writeState({ replace: !push });
        document.dispatchEvent(new CustomEvent('collection:rerendered'));
      } catch (error) {
        console.error('[searchanise-facets]', error);
      } finally {
        this.setLoading(false);
      }
    }

    async renderProducts(data) {
      const total = Number(data.totalItems) || 0;
      const ids = (data.items || []).map((item) => item.product_id);

      const list = document.getElementById('ProductsList');
      if (list) {
        const columns = await this.fetchCards(ids);
        const grid = document.getElementById('ProductGridContainer');

        grid?.querySelector('.empty-state')?.remove();

        if (columns.length) {
          list.replaceChildren(...columns);
          list.hidden = false;
        } else {
          list.replaceChildren();
          list.hidden = true;
          if (grid) grid.prepend(this.buildEmptyState(data));
        }
      }

      const count = document.getElementById('ProductCount');
      if (count) count.textContent = this.countLabel(total);

      this.renderPagination(total, document.getElementById('ProductGridContainer'), this.pageSize);
    }

    buildEmptyState(data, { hint = true } = {}) {
      const wrapper = el('div', 'empty-state flex flex-col items-center justify-center gap-6 text-center');
      const inner = el('div', 'gap-3 flex flex-col items-center justify-center text-center');
      inner.append(this.icon('empty-state'), el('p', 'h4', this.strings.noResults));
      if (hint) inner.append(el('p', 'm-0', this.strings.useFewerFilters));
      wrapper.append(inner);

      // Searchanise proposes terms that do have hits — the one moment where that
      // is worth more than another sentence telling the visitor to try again.
      const suggestions = (data?.suggestions || []).filter(Boolean).slice(0, 6);
      if (suggestions.length) {
        const box = el('div', 'flex flex-col items-center gap-3');
        box.append(el('p', 'm-0 font-body-bolder', this.strings.suggestions));

        const row = el('div', 'flex flex-wrap items-center justify-center gap-2');
        for (const term of suggestions) {
          const link = el('a', 'btn btn--secondary');
          link.href = '#';
          link.dataset.snizeSuggestion = term;
          link.append(el('span', 'btn__text', term));
          row.append(link);
        }

        box.append(row);
        wrapper.append(box);
      }

      return wrapper;
    }

    /** After a suggestion the field still shows what was typed, not what is shown. */
    syncSearchInputs() {
      if (this.scope !== 'search') return;
      document.querySelectorAll('.template-search__search input[name="q"]').forEach((input) => {
        input.value = this.state.q;
      });
    }

    scrollToGrid() {
      const target = document.querySelector('.collection');
      if (!target) return;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: window.FoxTheme?.config?.motionReduced ? 'auto' : 'smooth',
      });
    }

    // ---------------------------------------------------------------- events

    onChange(event) {
      const target = event.target;

      // Facet checkbox.
      const attribute = target?.dataset?.snizeAttribute;
      if (attribute) {
        const values = this.state.filters.get(attribute) || new Set();
        if (target.checked) values.add(target.value);
        else values.delete(target.value);

        if (values.size) this.state.filters.set(attribute, values);
        else this.state.filters.delete(attribute);

        this.state.page = 1;
        if (this.handBackIfUnfiltered()) return;

        this.ensureSortableOrder();
        this.apply();
        return;
      }

      // Price. <price-range> mirrors the sliders into these two number inputs and
      // re-dispatches change on them, so reading them covers both controls.
      if (target?.name === 'filter.v.price.gte' || target?.name === 'filter.v.price.lte') {
        const scope = target.closest('price-range');
        const min = Number(scope?.querySelector('[name="filter.v.price.gte"]')?.value || 0);
        const max = Number(scope?.querySelector('[name="filter.v.price.lte"]')?.value || this.priceMax || 0);

        this.state.price = min <= 0 && max >= (this.priceMax || 0) ? null : { min, max };
        this.state.page = 1;
        if (this.handBackIfUnfiltered()) return;

        this.ensureSortableOrder();
        this.apply();
        return;
      }

      // Sorting, always ours — never conditionally.
      //
      // The theme's FacetShort answers a sort by re-rendering the section
      // through the facet form, and renderFacets has replaced that form. Its
      // updateFormParams has no else branch, so with the form gone a sort on an
      // otherwise untouched collection did nothing at all, silently.
      if (target?.name === 'sort_by') {
        event.stopPropagation();
        this.state.sort = target.value;
        this.state.page = 1;
        // Picked by hand, so it survives the last filter being removed.
        this.autoSort = false;

        // Nothing filtered: the native grid is still on screen and Shopify sorts
        // it server-side — including the hand-sorted order we cannot express. So
        // this one goes through a reload rather than through Searchanise.
        //
        // It cannot be left to the theme: FacetShort answers a sort by
        // re-rendering the section through the facet form, and renderFacets has
        // replaced that form. Its updateFormParams has no else branch, so the
        // click would vanish without a trace.
        if (!this.ownsPage) {
          this.writeState();
          window.location.reload();
          return;
        }

        this.apply();
        // Stopping the event also stopped FacetShort's own handler, which is
        // what resizes the select to fit the chosen option.
        target.calcSelectWidth?.();
      }
    }

    onClick(event) {
      const tab = event.target.closest('[data-snize-tab]');
      if (tab) {
        event.preventDefault();
        if (tab.dataset.snizeTab === this.state.tab) return;
        this.state.tab = tab.dataset.snizeTab;
        // Each tab counts its own pages; carrying page 7 across would land on an
        // empty one.
        this.state.page = 1;
        this.apply();
        return;
      }

      const suggestion = event.target.closest('[data-snize-suggestion]');
      if (suggestion) {
        event.preventDefault();
        this.state.q = suggestion.dataset.snizeSuggestion;
        this.state.page = 1;
        this.apply();
        this.syncSearchInputs();
        return;
      }

      const remove = event.target.closest('[data-snize-remove]');
      if (remove) {
        event.preventDefault();
        const action = JSON.parse(remove.dataset.snizeRemove);

        if (action.all) {
          this.state.filters.clear();
          this.state.price = null;
          this.state.q = '';
        } else if (action.price) {
          this.state.price = null;
        } else if (action.query) {
          this.state.q = '';
        } else {
          const values = this.state.filters.get(action.attribute);
          values?.delete(action.value);
          if (!values?.size) this.state.filters.delete(action.attribute);
        }

        this.state.page = 1;

        // Everything cleared: hand the page back to the server-rendered view
        // rather than asking Searchanise for an unfiltered collection. On the
        // search page there is no such view, so ownsPage keeps us here.
        if (this.handBackIfUnfiltered()) return;

        this.ensureSortableOrder();
        this.apply();
        return;
      }

      const page = event.target.closest('[data-snize-page]');
      if (page) {
        event.preventDefault();
        this.state.page = Number(page.dataset.snizePage);
        this.apply();
        this.scrollToGrid();
      }
    }
  }

  customElements.define('searchanise-facets', SearchaniseFacets);

  // Back/forward: the URL is the source of truth, so re-read it and re-run.
  window.addEventListener('popstate', () => {
    const host = document.querySelector('searchanise-facets');
    if (!host?.ready) return;
    host.state = host.readState();
    if (host.ownsPage) host.apply({ push: false });
    else window.location.reload();
  });
}
